import type { Node, Schema } from "prosemirror-model"
import type { EditorState } from "prosemirror-state"
import type { EditorView } from "prosemirror-view"
import { Extension } from "@tiptap/core"
import { DOMParser as PmDOMParser } from "prosemirror-model"
import { Plugin, PluginKey } from "prosemirror-state"
import { Decoration, DecorationSet } from "prosemirror-view"

import type { HunkRange } from "../prosemirror-diff/proposal-steps"
import { diffDocs } from "../prosemirror-diff"

export interface DiffRenderOptions {
	// HTML string of the base (previous) content. When `hunkRanges` isn't
	// supplied, the diff is computed against this. Passed as HTML because the
	// rest of the help-center stack stores content as HTML.
	baseHtml: string | null
	// Per-hunk diff ranges in the editor's doc coordinate space. When non-null,
	// the extension renders one decoration per range tagged with
	// `data-proposal-id` + `data-hunk-id`, plus a per-hunk X control, so a
	// reviewer can discard a single change. When null, falls back to the legacy
	// "diff base vs current" behaviour.
	hunkRanges?: HunkRange[] | null
	// Fired when the cursor enters or leaves a decorated range. Coalesced to
	// one rAF so sibling-element transitions don't spam React state.
	onProposalHover?: ((proposalId: string | null) => void) | null
	// Fired when the X control for a hunk is clicked.
	onDiscardHunk?: ((hunkId: string) => void) | null
}

const diffRenderPluginKey = new PluginKey("proposal-diff-render")

// All diff styling lives in one injected stylesheet (not inline styles) so the
// dark-mode block below can override the colors — inline styles can't be themed.
// `box-decoration-break: clone` + the padding lets the background bridge the gap
// ProseMirror leaves between wrapped lines so the highlight reads as one ribbon.
// The `[data-proposal-active="true"]` rules light up the diff whose rail card is
// hovered (the attribute is flipped in React from `hoveredProposalId`).
const DIFF_STYLE_ID = "proposal-diff-styles"
function ensureDiffStylesInjected() {
	if (typeof document === "undefined") return
	if (document.getElementById(DIFF_STYLE_ID)) return
	const style = document.createElement("style")
	style.id = DIFF_STYLE_ID
	style.textContent = `
		[data-proposal-diff] {
			padding: 3px 1px;
			box-decoration-break: clone;
			-webkit-box-decoration-break: clone;
			transition: background-color 160ms cubic-bezier(.23, 1, .32, 1), color 160ms cubic-bezier(.23, 1, .32, 1);
		}
		[data-proposal-diff="insert"] {
			background-color: rgba(34, 197, 94, 0.18);
			color: #15803d;
		}
		[data-proposal-diff="delete"] {
			background-color: rgba(239, 68, 68, 0.15);
			color: #991b1b;
			border-radius: 2px;
			margin-right: 2px;
		}
		[data-proposal-diff="insert"][data-proposal-active="true"] {
			background-color: rgba(34, 197, 94, 0.24);
		}
		[data-proposal-diff="delete"][data-proposal-active="true"] {
			background-color: rgba(239, 68, 68, 0.22);
		}
		[data-proposal-diff="insert"][data-hunk-active="true"] {
			background-color: rgba(34, 197, 94, 0.45);
			color: #14532d;
		}
		[data-proposal-diff="delete"][data-hunk-active="true"] {
			background-color: rgba(239, 68, 68, 0.4);
			color: #7f1d1d;
		}
		.proposal-hunk-anchor {
			position: relative;
			display: inline-block;
			width: 0;
			height: 0;
			vertical-align: text-bottom;
		}
		.proposal-hunk-menu {
			position: absolute;
			bottom: 100%;
			left: 0;
			margin-bottom: 14px;
			z-index: 30;
			display: inline-flex;
			padding: 4px;
			border: 1px solid #e5e5e5;
			border-radius: 10px;
			background: #ffffff;
			box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(208, 209, 210, 0.3), 0 8px 24px -8px rgba(0, 0, 0, 0.18);
			opacity: 0;
			pointer-events: none;
			transform: translateY(2px);
			will-change: opacity, transform;
			transition: opacity 140ms cubic-bezier(.23, 1, .32, 1), transform 140ms cubic-bezier(.23, 1, .32, 1);
		}
		.proposal-hunk-menu[data-hunk-hovered="true"] {
			opacity: 1;
			pointer-events: auto;
			transform: translateY(0);
		}
		.proposal-hunk-skip {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			padding: 5px 9px;
			border: none;
			border-radius: 6px;
			background: transparent;
			font-size: 13px;
			font-weight: 500;
			line-height: 1;
			white-space: nowrap;
			color: #707476;
			transition: background-color 120ms cubic-bezier(.23, 1, .32, 1), color 120ms cubic-bezier(.23, 1, .32, 1);
		}
		.proposal-hunk-skip:hover {
			background: #f4f5f5;
			color: #0f0f12;
		}
		.proposal-hunk-skip:active {
			transform: scale(0.97);
		}
		.proposal-hunk-skip-icon {
			flex-shrink: 0;
			opacity: 0.75;
		}
		@media (prefers-color-scheme: dark) {
			[data-proposal-diff="insert"] {
				background-color: rgba(34, 197, 94, 0.22);
				color: #86efac;
			}
			[data-proposal-diff="delete"] {
				background-color: rgba(248, 113, 113, 0.2);
				color: #fca5a5;
			}
			[data-proposal-diff="insert"][data-proposal-active="true"] {
				background-color: rgba(34, 197, 94, 0.32);
			}
			[data-proposal-diff="delete"][data-proposal-active="true"] {
				background-color: rgba(248, 113, 113, 0.3);
			}
			[data-proposal-diff="insert"][data-hunk-active="true"] {
				background-color: rgba(34, 197, 94, 0.5);
				color: #f0fdf4;
			}
			[data-proposal-diff="delete"][data-hunk-active="true"] {
				background-color: rgba(248, 113, 113, 0.5);
				color: #fef2f2;
			}
			.proposal-hunk-menu {
				background: #151518;
				border-color: #1a1a1e;
				box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(48, 48, 49, 0.4), 0 12px 32px -10px rgba(0, 0, 0, 0.6);
			}
			.proposal-hunk-skip {
				color: #e5e5e5;
			}
			.proposal-hunk-skip:hover {
				background: #1a1a1e;
				color: #f4f5f5;
			}
		}
	`
	document.head.appendChild(style)
}

function htmlToDoc(html: string, schema: Schema): Node | null {
	try {
		if (typeof document === "undefined") return null
		const container = document.createElement("div")
		container.innerHTML = html
		return PmDOMParser.fromSchema(schema).parse(container)
	} catch (err) {
		console.warn("proposal-diff: failed to parse baseHtml", err)
		return null
	}
}

function buildDeleteWidget(
	oldText: string,
	proposalId: string | null,
	hunkId?: string | null,
) {
	return () => {
		const span = document.createElement("span")
		span.dataset.proposalDiff = "delete"
		if (proposalId) span.dataset.proposalId = proposalId
		if (hunkId) span.dataset.hunkId = hunkId
		span.textContent = oldText
		return span
	}
}

// Nucleo IconChevronRightToLineOutline18 (skip-to-end), inlined because the
// control is built as raw DOM inside the editor, outside React.
const SKIP_ICON_SVG = `<svg class="proposal-hunk-skip-icon" width="13" height="13" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3.75 2.75 10 9 3.75 15.25"/><line x1="14.25" y1="2.75" x2="14.25" y2="15.25"/></svg>`

function buildHunkControl(hunkId: string) {
	return () => {
		// Zero-size inline host so the floating menu doesn't shift the diff text;
		// the menu is absolutely positioned above the change, styled to match the
		// editor's bubble menu.
		const anchor = document.createElement("span")
		anchor.className = "proposal-hunk-anchor"
		anchor.dataset.hunkId = hunkId

		const menu = document.createElement("span")
		menu.className = "proposal-hunk-menu"
		menu.dataset.hunkId = hunkId

		const skip = document.createElement("button")
		skip.type = "button"
		skip.className = "proposal-hunk-skip"
		skip.dataset.hunkControl = ""
		skip.dataset.hunkId = hunkId
		skip.setAttribute("aria-label", "Skip this change")
		skip.innerHTML = `${SKIP_ICON_SVG}<span>Skip</span>`

		menu.appendChild(skip)
		anchor.appendChild(menu)
		return anchor
	}
}

function computeDecorationsFromBase(state: EditorState, baseDoc: Node | null) {
	if (!baseDoc) return DecorationSet.empty

	let changes
	try {
		changes = diffDocs(baseDoc, state.doc)
	} catch (err) {
		console.warn("proposal-diff: diffDocs failed", err)
		return DecorationSet.empty
	}

	const docSize = state.doc.content.size
	const clamp = (pos: number) => Math.max(0, Math.min(pos, docSize))
	const decorations: Decoration[] = []

	for (const change of changes) {
		if (change.type === "insert") {
			const from = clamp(change.rangeB.from)
			const to = clamp(change.rangeB.to)
			if (to > from) {
				decorations.push(
					Decoration.inline(from, to, {
						"data-proposal-diff": "insert",
					}),
				)
			}
		} else if (change.type === "delete") {
			const oldText = baseDoc.textBetween(
				change.rangeA.from,
				change.rangeA.to,
				" ",
				" ",
			)
			if (oldText.trim().length > 0) {
				decorations.push(
					Decoration.widget(
						clamp(change.rangeB.pos),
						buildDeleteWidget(oldText, null),
					),
				)
			}
		} else if (change.type === "replace") {
			const from = clamp(change.rangeB.from)
			const to = clamp(change.rangeB.to)
			if (to > from) {
				decorations.push(
					Decoration.inline(from, to, {
						"data-proposal-diff": "insert",
					}),
				)
			}
			const oldText = baseDoc.textBetween(
				change.rangeA.from,
				change.rangeA.to,
				" ",
				" ",
			)
			if (oldText.trim().length > 0) {
				decorations.push(
					Decoration.widget(from, buildDeleteWidget(oldText, null)),
				)
			}
		}
	}

	return DecorationSet.create(state.doc, decorations)
}

function computeDecorationsFromHunkRanges(
	state: EditorState,
	hunkRanges: HunkRange[],
	renderControls: boolean,
) {
	const docSize = state.doc.content.size
	const clamp = (pos: number) => Math.max(0, Math.min(pos, docSize))
	const decorations: Decoration[] = []
	// Lowest anchor position per hunk, so each hunk gets exactly one X control.
	const anchorByHunk = new Map<string, number>()
	const noteAnchor = (hunkId: string, pos: number) => {
		const existing = anchorByHunk.get(hunkId)
		if (existing == null || pos < existing) anchorByHunk.set(hunkId, pos)
	}

	for (const { proposalId, hunkId, range } of hunkRanges) {
		if (range.kind === "insert") {
			const from = clamp(range.from)
			const to = clamp(range.to)
			if (to > from) {
				decorations.push(
					Decoration.inline(from, to, {
						"data-proposal-diff": "insert",
						"data-proposal-id": proposalId,
						"data-hunk-id": hunkId,
					}),
				)
				noteAnchor(hunkId, from)
			}
		} else if (range.kind === "delete") {
			if (range.oldText.trim().length > 0) {
				const pos = clamp(range.pos)
				decorations.push(
					Decoration.widget(
						pos,
						buildDeleteWidget(range.oldText, proposalId, hunkId),
					),
				)
				noteAnchor(hunkId, pos)
			}
		} else {
			const from = clamp(range.from)
			const to = clamp(range.to)
			if (to > from) {
				decorations.push(
					Decoration.inline(from, to, {
						"data-proposal-diff": "insert",
						"data-proposal-id": proposalId,
						"data-hunk-id": hunkId,
					}),
				)
			}
			if (range.oldText.trim().length > 0) {
				decorations.push(
					Decoration.widget(
						from,
						buildDeleteWidget(range.oldText, proposalId, hunkId),
					),
				)
			}
			noteAnchor(hunkId, from)
		}
	}

	if (renderControls) {
		for (const [hunkId, anchor] of anchorByHunk) {
			decorations.push(
				Decoration.widget(anchor, buildHunkControl(hunkId), {
					side: -1,
					key: `hunk-x-${hunkId}`,
					ignoreSelection: true,
				}),
			)
		}
	}

	return DecorationSet.create(state.doc, decorations)
}

export const DiffRenderExtension = Extension.create<DiffRenderOptions>({
	name: "proposalDiffRender",

	addOptions() {
		return {
			baseHtml: null,
			hunkRanges: null,
			onProposalHover: null,
			onDiscardHunk: null,
		}
	},

	addProseMirrorPlugins() {
		const { baseHtml, hunkRanges, onProposalHover, onDiscardHunk } =
			this.options
		const useHunks = hunkRanges != null
		const renderControls = onDiscardHunk != null

		ensureDiffStylesInjected()

		const computeDecorations = (state: EditorState) => {
			if (useHunks && hunkRanges) {
				return computeDecorationsFromHunkRanges(
					state,
					hunkRanges,
					renderControls,
				)
			}
			const baseDoc = baseHtml ? htmlToDoc(baseHtml, state.schema) : null
			return computeDecorationsFromBase(state, baseDoc)
		}

		// Coalesce mouseover spam from sibling elements: only the latest
		// distinct proposal id (or null) per animation frame is reported.
		let lastReportedId: string | null = null
		let pendingId: string | null = null
		let rafHandle: number | null = null
		const reportHover = (id: string | null) => {
			pendingId = id
			if (rafHandle != null) return
			rafHandle = requestAnimationFrame(() => {
				rafHandle = null
				if (pendingId !== lastReportedId) {
					lastReportedId = pendingId
					onProposalHover?.(pendingId)
				}
			})
		}

		const toggleAttr = (root: HTMLElement, attr: string, id: string | null) => {
			for (const el of root.querySelectorAll(`[${attr}="true"]`)) {
				el.removeAttribute(attr)
			}
			if (!id) return
			for (const el of root.querySelectorAll(`[data-hunk-id="${id}"]`)) {
				el.setAttribute(attr, "true")
			}
		}

		// Diff highlight: clears the instant the cursor leaves the change.
		let highlightedHunkId: string | null = null
		const setHighlightedHunk = (root: HTMLElement, id: string | null) => {
			if (id === highlightedHunkId) return
			highlightedHunkId = id
			toggleAttr(root, "data-hunk-active", id)
		}

		// Skip chip: its hide lingers briefly so the cursor can cross the gap up
		// to the chip (which sits beside the change) without it vanishing mid-reach.
		const CHIP_HIDE_DELAY_MS = 250
		let chipHunkId: string | null = null
		let chipHideTimer: ReturnType<typeof setTimeout> | null = null
		const applyChipHunk = (root: HTMLElement, id: string | null) => {
			if (id === chipHunkId) return
			chipHunkId = id
			toggleAttr(root, "data-hunk-hovered", id)
		}
		const setChipHunk = (root: HTMLElement, id: string | null) => {
			if (chipHideTimer != null) {
				clearTimeout(chipHideTimer)
				chipHideTimer = null
			}
			if (id) {
				applyChipHunk(root, id)
				return
			}
			chipHideTimer = setTimeout(() => {
				chipHideTimer = null
				applyChipHunk(root, null)
			}, CHIP_HIDE_DELAY_MS)
		}

		const handleDOMEvents = {
			mousedown(_view: EditorView, event: MouseEvent) {
				if (!renderControls) return false
				const target = event.target as Element | null
				const control =
					target && typeof target.closest === "function"
						? target.closest("[data-hunk-control]")
						: null
				if (!control) return false
				const id = control.getAttribute("data-hunk-id")
				event.preventDefault()
				if (id) onDiscardHunk?.(id)
				return true
			},
			mouseover(view: EditorView, event: MouseEvent) {
				const target = event.target as Element | null
				const close = (sel: string) =>
					target && typeof target.closest === "function"
						? target.closest(sel)
						: null
				reportHover(
					close("[data-proposal-id]")?.getAttribute("data-proposal-id") ?? null,
				)
				if (useHunks) {
					// Highlight the change while over either the change or its menu.
					const hunkId =
						close("[data-hunk-id]")?.getAttribute("data-hunk-id") ?? null
					setHighlightedHunk(view.dom, hunkId)
					setChipHunk(view.dom, hunkId)
				}
				return false
			},
			mouseout(view: EditorView, event: MouseEvent) {
				const related = (event.relatedTarget as Element | null) ?? null
				const inside = (sel: string) =>
					related && typeof related.closest === "function"
						? related.closest(sel)
						: null
				if (!inside("[data-proposal-id]")) reportHover(null)
				if (useHunks && !inside("[data-hunk-id]")) {
					setHighlightedHunk(view.dom, null)
					setChipHunk(view.dom, null)
				}
				return false
			},
		}

		return [
			new Plugin<DecorationSet>({
				key: diffRenderPluginKey,
				state: {
					init: (_, state) => computeDecorations(state),
					apply: (tr, old, _oldState, newState) =>
						tr.docChanged ? computeDecorations(newState) : old,
				},
				props: {
					decorations(state) {
						return this.getState(state)
					},
					...((onProposalHover || useHunks) && { handleDOMEvents }),
				},
			}),
		]
	},
})
